import React, { useEffect, useRef } from 'react';
import { useGallery } from '../../context/GalleryContext';
import { useUI } from '../../context/UIContext';
import { NavTree } from './NavTree';
import { useActiveSectionPath } from './useActiveSectionPath';
import { CloseIcon } from '../icons';

// Slide-in nav drawer for < lg. Focus is trapped while open; Esc and the
// backdrop close it; body scroll is locked; focus returns to the opener on close;
// selecting a leaf closes it (NavTree onNavigate).
export const MobileNavDrawer = () => {
    const { navTree } = useGallery();
    const { drawerOpen, closeDrawer } = useUI();
    const activePath = useActiveSectionPath();
    const rootRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const openerRef = useRef<HTMLElement | null>(null);

    // Keep the (off-screen) drawer out of the tab order + a11y tree while closed.
    useEffect(() => {
        const el = rootRef.current;
        if (!el) return;
        if (drawerOpen) el.removeAttribute('inert');
        else el.setAttribute('inert', '');
    }, [drawerOpen]);

    useEffect(() => {
        if (!drawerOpen) return;
        openerRef.current = document.activeElement as HTMLElement;
        const { body } = document;
        const prevOverflow = body.style.overflow;
        body.style.overflow = 'hidden';

        const panel = panelRef.current;
        const focusables = () => panel?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? [];
        (focusables()[0] ?? panel)?.focus();

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                closeDrawer();
                return;
            }
            if (e.key === 'Tab') {
                const items = focusables();
                if (!items.length) return;
                const first = items[0];
                const last = items[items.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            body.style.overflow = prevOverflow;
            openerRef.current?.focus();
        };
    }, [drawerOpen, closeDrawer]);

    return (
        <div
            ref={rootRef}
            className={`fixed inset-0 z-50 lg:hidden ${drawerOpen ? '' : 'pointer-events-none'}`}
            aria-hidden={!drawerOpen}
        >
            <div
                onClick={closeDrawer}
                className={`absolute inset-0 bg-black/20 motion-safe:transition-opacity motion-safe:duration-300 ${drawerOpen ? 'opacity-100' : 'opacity-0'}`}
            />
            <div
                id="mobile-nav-drawer"
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label="Sections menu"
                tabIndex={-1}
                className={`absolute inset-y-0 left-0 w-72 max-w-[80%] bg-canvas px-8 pb-8 pt-6 shadow-xl motion-safe:transition-transform motion-safe:duration-300 ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`}
            >
                <div className="mb-6 flex justify-end">
                    <button type="button" onClick={closeDrawer} aria-label="Close menu" className="text-ink">
                        <CloseIcon size={22} />
                    </button>
                </div>
                <nav aria-label="Sections menu">
                    <NavTree nodes={navTree} activePath={activePath} onNavigate={closeDrawer} />
                </nav>
            </div>
        </div>
    );
};
